import { schoolGradesApi } from '@/services/schoolGradesApi'

const state = () => ({
  items: [],
  selectedItem: null,
  loading: false,
  error: null
})

const getters = {
  allGrades: (state) => state.items,
  activeGrades: (state) => state.items.filter(grade => grade.is_active),
  inactiveGrades: (state) => state.items.filter(grade => !grade.is_active),
  isLoading: (state) => state.loading,
  hasError: (state) => state.error,
  getSelectedItem: (state) => state.selectedItem
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },

  SET_ERROR(state, error) {
    state.error = error
  },

  SET_ITEMS(state, grades) {
    state.items = grades
  },

  SET_SELECTED_ITEM(state, grade) {
    state.selectedItem = grade
  },

  ADD_GRADE(state, grade) {
    state.items.unshift(grade)
  },

  UPDATE_GRADE(state, updatedGrade) {
    const index = state.items.findIndex(grade => grade.id === updatedGrade.id)
    if (index !== -1) {
      state.items.splice(index, 1, updatedGrade)
    }
  },

  REMOVE_GRADE(state, gradeId) {
    state.items = state.items.filter(grade => grade.id !== gradeId)
  },

  CLEAR_ERROR(state) {
    state.error = null
  },

  CLEAR_SELECTED_ITEM(state) {
    state.selectedItem = null
  }
}

const actions = {
  async fetchGrades({ commit, state }, params = {}) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await schoolGradesApi.getSchoolGrades(params)

      // Handle both direct response and nested data structure
      const responseData = response.data || response
      const gradesData = responseData.data || responseData

      commit('SET_ITEMS', gradesData)

      return { success: true, data: responseData }
    } catch (error) {
      let errorMessage = 'Error al cargar los grados'

      // Handle specific error cases
      if (error.status === 403) {
        errorMessage = 'No tienes permisos para realizar esta acción.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getGrade({ commit }, gradeId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await schoolGradesApi.getSchoolGrade(gradeId)
      const gradeData = response.data || response
      commit('SET_SELECTED_ITEM', gradeData)

      return { success: true, data: gradeData }
    } catch (error) {
      let errorMessage = 'Error al cargar el grado'

      if (error.status === 403) {
        errorMessage = 'No tienes permisos para realizar esta acción.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createGrade({ commit }, gradeData) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await schoolGradesApi.createSchoolGrade(gradeData)
      const createdGrade = response.data || response
      commit('ADD_GRADE', createdGrade)

      return { success: true, data: createdGrade, message: 'Grado creado exitosamente' }
    } catch (error) {
      let errorMessage = 'Error al crear el grado'

      if (error.status === 403) {
        errorMessage = 'No tienes permisos para realizar esta acción.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateGrade({ commit, state }, { id, gradeData }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await schoolGradesApi.updateSchoolGrade(id, gradeData)
      const updatedGrade = response.data || response
      commit('UPDATE_GRADE', updatedGrade)

      // Update selected item if it's the one being edited
      if (state.selectedItem && state.selectedItem.id === id) {
        commit('SET_SELECTED_ITEM', updatedGrade)
      }

      return { success: true, data: updatedGrade, message: 'Grado actualizado exitosamente' }
    } catch (error) {
      let errorMessage = 'Error al actualizar el grado'

      if (error.status === 403) {
        errorMessage = 'No tienes permisos para realizar esta acción.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteGrade({ commit }, gradeId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      await schoolGradesApi.deleteSchoolGrade(gradeId)
      commit('REMOVE_GRADE', gradeId)

      return { success: true, message: 'Grado eliminado exitosamente' }
    } catch (error) {
      let errorMessage = 'Error al eliminar el grado'

      if (error.status === 403) {
        errorMessage = 'No tienes permisos para realizar esta acción.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  setSelectedItem({ commit }, grade) {
    commit('SET_SELECTED_ITEM', grade)
  },

  clearSelectedItem({ commit }) {
    commit('CLEAR_SELECTED_ITEM')
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}